'use client';
import React, { useEffect } from 'react';

// import { FilterKey, FiltersRecord, SearchParamsType } from '@/app/types/searchparams';
import { FilterKey, FilterOptionKeys, FilterOptionValue, SearchParams } from '@mybestbrands/types';
import { DimmerScreen } from '@/app/ui/components/filters/dimmerScreen';
import { SizesFilter } from '@/app/ui/components/filters/SizesFilter';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { BrandsFilter } from '@/app/ui/components/filters/BrandsFilter';
import PriceFilter from '@/app/ui/components/filters/PriceFilter';
import SaleFilter from '@/app/ui/components/filters/SaleFilter';
import { ColorsFilter } from '@/app/ui/components/filters/ColorsFilter';
import { MaterialsFilter } from '@/app/ui/components/filters/MaterialsFilter';
import { PatternsFilter } from '@/app/ui/components/filters/PatternsFilter';
import { ShopsFilter } from '@/app/ui/components/filters/ShopsFilter';
import { CategoriesFilter } from '@/app/ui/components/filters/CategoriesFilter';
import { SortingFilter } from '@/app/ui/components/filters/SortFilter';
import CouponFilter from '@/app/ui/components/filters/CouponFilter';
import HottestSaleFilter from '@/app/ui/components/filters/HottestSaleFilter';

interface Props {
  sorting?: FilterOptionValue | undefined;
  sizes?: FilterOptionValue | undefined;
  price?: FilterOptionValue | undefined;
  brands?: FilterOptionValue | undefined;
  materials?: FilterOptionValue | undefined;
  retailers?: FilterOptionValue | undefined;
  patterns?: FilterOptionValue | undefined;
  colors?: FilterOptionValue | undefined;
  categories?: FilterOptionValue | undefined;
  searchParams: SearchParams;
  disabledFilters?: FilterOptionKeys[];
  sale?: FilterOptionValue | undefined;
  coupon?: FilterOptionValue | undefined;
  hottest_sale?: FilterOptionValue | undefined;
  blackFriday?: boolean | undefined;
}

export function Filters(props: Props) {
  const [activeFilter, setActiveFilter] = React.useState('');
  const pathSearchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const { disabledFilters } = props;
  const [searchParams, setSearchParams] = React.useState<SearchParams>(props.searchParams);

  //@ts-ignore
  const showSale =
    (!!props?.sale?.options?.length || !!props?.sale?.options?.filter((el) => el.id !== 'sale').length) &&
    !searchParams.filters.hottest_sale;

  const showHottestSale = !!props?.hottest_sale?.options?.length && !!props?.blackFriday;
  const showCoupon = !!props?.coupon?.options?.length;

  useEffect(() => {
    setSearchParams(props.searchParams);
  }, [props]);

  function onDimmerClick() {
    onApplySearchFilters();
  }

  function onSearchParamsUpdate(
    searchParamKey: FilterOptionKeys,
    value: string[] | number[] | number
  ): SearchParams | null {
    if (searchParams && searchParamKey in searchParams?.filters) {
      const newFilters = { ...searchParams, filters: { ...searchParams.filters, [searchParamKey]: value } };
      setSearchParams({ ...newFilters });
      return newFilters;
    }
    return null;
  }

  function toggleActiveFilter(filter: string) {
    if (activeFilter === filter) {
      setActiveFilter('');
    } else {
      setActiveFilter(filter);
    }
  }

  function onApplySearchFilters(newSp?: SearchParams | null) {
    const filters = newSp?.filters ?? searchParams?.filters;
    const params = new URLSearchParams(pathSearchParams ?? '');
    const page = params.get('page');

    const selectedFilters = Object.entries(filters ?? {})
      .filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        return !!value;
      })
      .reduce((acc, [key, value]) => ({ ...acc, [key as FilterKey]: value }), {});

    //On each filter change we have to remove the page param
    //because we want to start from the first page
    if (page) params.delete('page');

    params.set('filters', JSON.stringify(selectedFilters));
    const url = `${pathname}?${params.toString()}`;

    setActiveFilter('');
    router.push(url);
  }

  return (
    <>
      <DimmerScreen isActive={activeFilter !== ''} onClick={onDimmerClick} />
      <SortingFilter
        toggleFilterOptions={() => toggleActiveFilter(FilterOptionKeys.SORTING)}
        filter={props.sorting}
        isActive={activeFilter === FilterOptionKeys.SORTING}
      />
      <CategoriesFilter
        toggleFilterOptions={() => toggleActiveFilter(FilterOptionKeys.CATEGORIES)}
        onApplySearchFilters={onApplySearchFilters}
        onFilterUpdate={(value: string[]) => onSearchParamsUpdate(FilterOptionKeys.CATEGORIES, value)}
        filter={props.categories}
        isActive={activeFilter === FilterOptionKeys.CATEGORIES}
      />
      <SizesFilter
        onApplySearchFilters={onApplySearchFilters}
        onFilterUpdate={(value: string[]) => onSearchParamsUpdate(FilterOptionKeys.SIZES, value)}
        filter={props.sizes}
        isActive={activeFilter === FilterOptionKeys.SIZES}
        toggleFilterOptions={() => toggleActiveFilter(FilterOptionKeys.SIZES)}
      />
      <BrandsFilter
        filter={props.brands}
        onApplySearchFilters={onApplySearchFilters}
        onFilterUpdate={(value: string[]) => onSearchParamsUpdate(FilterOptionKeys.BRANDS, value)}
        isActive={activeFilter === FilterOptionKeys.BRANDS}
        toggleFilterOptions={() => toggleActiveFilter(FilterOptionKeys.BRANDS)}
      />
      <PriceFilter
        toggleFilterOptions={() => toggleActiveFilter(FilterOptionKeys.PRICE)}
        filter={props.price}
        onApplySearchFilters={onApplySearchFilters}
        onFilterUpdate={(value: string[] | number[] | number) => onSearchParamsUpdate(FilterOptionKeys.PRICE, value)}
        isActive={activeFilter === FilterOptionKeys.PRICE}
      />
      {!disabledFilters?.includes(FilterOptionKeys.SALE) && showSale && (
        <SaleFilter isActive={!!searchParams?.filters?.sale} />
      )}
      {showHottestSale && <HottestSaleFilter isActive={!!searchParams?.filters?.hottest_sale} />}
      <ColorsFilter
        filter={props.colors}
        onApplySearchFilters={onApplySearchFilters}
        onFilterUpdate={(value: string[]) => onSearchParamsUpdate(FilterOptionKeys.COLORS, value)}
        isActive={activeFilter === FilterOptionKeys.COLORS}
        toggleFilterOptions={() => toggleActiveFilter(FilterOptionKeys.COLORS)}
      />
      <MaterialsFilter
        filter={props.materials}
        onApplySearchFilters={onApplySearchFilters}
        onFilterUpdate={(value: string[]) => onSearchParamsUpdate(FilterOptionKeys.MATERIALS, value)}
        isActive={activeFilter === FilterOptionKeys.MATERIALS}
        toggleFilterOptions={() => toggleActiveFilter(FilterOptionKeys.MATERIALS)}
      />
      <PatternsFilter
        filter={props.patterns}
        onApplySearchFilters={onApplySearchFilters}
        onFilterUpdate={(value: string[]) => onSearchParamsUpdate(FilterOptionKeys.PATTERNS, value)}
        isActive={activeFilter === FilterOptionKeys.PATTERNS}
        toggleFilterOptions={() => toggleActiveFilter(FilterOptionKeys.PATTERNS)}
      />
      <ShopsFilter
        filter={props.retailers}
        onApplySearchFilters={onApplySearchFilters}
        onFilterUpdate={(value: string[]) => onSearchParamsUpdate(FilterOptionKeys.RETAILERS, value)}
        isActive={activeFilter === FilterOptionKeys.RETAILERS}
        toggleFilterOptions={() => toggleActiveFilter(FilterOptionKeys.RETAILERS)}
      />
      {showCoupon && <CouponFilter isActive={!!searchParams?.filters?.coupon} />}
    </>
  );
}
